import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Button
} from 'reactstrap';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as Icon from 'react-feather';
import html2canvas from 'html2canvas';
import { useDispatch } from 'react-redux';

import { callexCallDetailsGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import CallexLoader from '../../../components/CallexLoader';

const CallexCallDetailsModal = ({ isOpen, onClose, callId }) => {
  const dispatch = useDispatch();

  const chatRef = useRef(null);

  const [mode, setMode] = useState('simple');
  const [activeTab, setActiveTab] = useState('chat');
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCallDetails = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(callexCallDetailsGet(callId)).unwrap();
      if (response && response.success) {
        setDetails(response?.data || null);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (callId && isOpen) {
      fetchCallDetails();
    }
  }, [callId, isOpen]);

  /* ================= FORMATTERS ================= */

  const formattedCallTime = useMemo(() => {
    if (!details?.startedAt) return '-';
    // Handle Firestore-style timestamp or string
    const date = details.startedAt._seconds
      ? new Date(details.startedAt._seconds * 1000)
      : new Date(details.startedAt);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [details]);

  const sentimentColor =
    details?.sentiment === 'Positive'
      ? 'success'
      : details?.sentiment === 'Negative'
        ? 'danger'
        : 'warning';

  /* ================= DATA ADAPTERS ================= */

  const messages = useMemo(() => {
    const rawMessages = details?.transcriptMessages || [];
    return rawMessages.map((msg) => {
      const ts = msg.timestamp || details?.startedAt?._seconds;
      return {
        type:
          msg.role === 'ai' || msg.type === 'assistant' || msg.role === 'model'
            ? 'assistant'
            : 'user',
        text: msg.text,
        timestamp: ts ? ts * 1000 : Date.now()
      };
    });
  }, [details]);

  /* ================= RENDER CHAT ================= */

  const renderChat = () => {
    if (!messages.length) return <div className="p-3 text-center">No transcript available</div>;

    return (
      <div className="chat-wrapper" ref={chatRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`chat-row ${msg.type}`}>
            {msg.type === 'assistant' && (
              <div className="avatar assistant">
                <Icon.Headphones size={16} />
              </div>
            )}

            <div className="message-group">
              <div className="chat-bubble">{msg.text}</div>
              <div className="chat-time">
                {new Date(msg.timestamp).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>

            {msg.type === 'user' && (
              <div className="avatar user">
                <Icon.User size={16} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  /* ================= RENDER ANALYSIS ================= */
  const renderAnalysis = () => {
    const { highlighted_points, ...otherVariables } = details.structuredData || {};
    const hasOtherVariables = Object.keys(otherVariables).length > 0;
    const hasHighlightedPoints = Array.isArray(highlighted_points) && highlighted_points.length > 0;

    return (
      <div className="analysis-section">
        <Row>
          {/* 1. Call Analysis (Full Width) */}
          <Col md="12">
            <div className="analysis-card">
              <div className="analysis-header">Call Analysis</div>

              <div className="sentiment-row">
                <span>Sentiment Score</span>
                <span className={`sentiment-pill ${sentimentColor}`}>
                  {details?.sentiment || 'Unclear'}
                </span>
              </div>

              <div className="analysis-box">
                <div className="analysis-subtitle">Analysis Details</div>
                <div className="analysis-description">
                  {details?.summary || 'No summary available'}
                </div>
              </div>
            </div>
          </Col>

          {/* 2. Extracted Variables (Full Width) */}
          <Col md="12" className="mt-3">
            <div className="analysis-card">
              <div className="analysis-header">Extracted Variables</div>

              {hasOtherVariables && (
                <div className="variables-grid mb-3">
                  {Object.entries(otherVariables).map(([key, value]) => (
                    <div key={key} className="variable-item">
                      <div className="variable-label">{key.replaceAll('_', ' ')}</div>
                      <div className="variable-value">
                        {typeof value === 'boolean'
                          ? value
                            ? 'Yes'
                            : 'No'
                          : value || 'Not provided'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hasHighlightedPoints && (
                <div className="table-responsive">
                  {hasOtherVariables && (
                    <>
                      <hr />
                      <div className="analysis-header my-2">Highlighted Points</div>
                    </>
                  )}
                  <table className="latency-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Question / Topic</th>
                        <th>Customer Answer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {highlighted_points.map((point, idx) => (
                        <tr key={idx}>
                          <td className="fw-bold">{point.question_or_topic}</td>
                          <td>{point.customer_answer}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!hasOtherVariables && !hasHighlightedPoints && (
                <div className="p-3 text-center">No variables extracted</div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  /* ================= RENDER EVALUATIONS ================= */
  const renderEvaluations = () => (
    <div className="analysis-section">
      <Row>
        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Metrics</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Intent Score</div>
                <div className="variable-value">{details?.metric_score_intent ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Relevance Score</div>
                <div className="variable-value">{details?.metric_score_relevance ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Latency Score</div>
                <div className="variable-value">{details?.metric_score_latency ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Coherence Score</div>
                <div className="variable-value">{details?.metric_score_coherence ?? 0}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Metrics</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Sentiment</div>
                <div className="variable-value">{details?.sentiment || 'N/A'}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">CQS Score</div>
                <div className="variable-value">{details?.cqs_score ?? 0}</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  /* ================= RENDER LLM ================= */
  const renderLlmDetails = () => (
    <div className="analysis-section">
      <Row>
        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Model Details</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Model</div>
                <div className="variable-value">{details?.model_name ?? 'N/A'}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Vendor</div>
                <div className="variable-value">{'N/A'}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Type</div>
                <div className="variable-value">{details?.model_type ?? 'N/A'}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="analysis-card">
            <div className="analysis-header">Usage & Cost</div>
            <div className="variables-grid">
              <div className="variable-item">
                <div className="variable-label">Total Tokens</div>
                <div className="variable-value">{details?.total_tokens ?? 0}</div>
              </div>

              <div className="variable-item">
                <div className="variable-label">Cost</div>
                <div className="variable-value">
                  ${details?.aggregated_estimated_cost?.toFixed(3) ?? '0.000'}
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col md="12">
          <div
            className="analysis-card"
            style={{ marginTop: '1.5rem', marginBottom: '1.5rem', height: 'max-content' }}>
            <div className="analysis-header">Usage & Cost</div>
            <div className="variable-label" style={{ textTransform: 'none', fontSize: '12px' }}>
              Duration: {formatTime(details?.duration)}
            </div>
            <div className="variable-value">{details?.transcript}</div>
          </div>
        </Col>
      </Row>
    </div>
  );

  /* ================= RENDER LATENCY ================= */

  const renderLatency = () => {
    // ---------- Helpers ----------
    const getPercentile = (arr, percentile) => {
      if (!arr.length) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = Math.floor((percentile / 100) * (sorted.length - 1));
      return sorted[index] || 0;
    };

    const getStdDev = (arr) => {
      if (!arr.length) return 0;
      const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
      const variance = arr.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / arr.length;
      return Math.sqrt(variance);
    };

    // ---------- Grouped Interactions (Reuse logic from interactionTab) ----------
    const interactions = [];
    for (let i = 0; i < messages.length; i++) {
      const current = messages[i];
      if (current.type === 'user') {
        const next = messages[i + 1];
        interactions.push({
          interaction_sequence: interactions.length + 1,
          latency_llm: details?.latency_llm || 0, // Mocking or using from detail
          tts_time: details?.latency_tts || 0,
          tts_speaking_duration: 0,
          total_tokens: details?.total_tokens || 0,
          type: 'pair'
        });
        if (next?.type === 'assistant') i++;
      }
    }

    // ---------- Extract Values ----------
    const llmLatencies = interactions.map((i) => i.latency_llm || 0);
    const ttsLatencies = interactions.map((i) => i.tts_time || 0);
    const ttsDurations = interactions.map((i) => i.tts_speaking_duration || 0);

    // ---------- Totals ----------
    const totalLLM = llmLatencies.reduce((a, b) => a + b, 0);
    const totalTTS = ttsLatencies.reduce((a, b) => a + b, 0);
    const totalTTSDuration = ttsDurations.reduce((a, b) => a + b, 0);

    // ---------- Averages ----------
    const avgLLM = llmLatencies.length > 0 ? totalLLM / llmLatencies.length : 0;
    const avgTTS = ttsLatencies.length > 0 ? totalTTS / ttsLatencies.length : 0;
    const avgTTSDuration = ttsDurations.length > 0 ? totalTTSDuration / ttsDurations.length : 0;

    const avgTotal =
      interactions.length > 0
        ? interactions.reduce((acc, row) => acc + (row.latency_llm || 0) + (row.tts_time || 0), 0) /
          interactions.length
        : 0;

    // ---------- Statistics ----------
    const llmP50 = getPercentile(llmLatencies, 50);
    const llmP95 = getPercentile(llmLatencies, 95);
    const llmStd = getStdDev(llmLatencies);

    const ttsP50 = getPercentile(ttsLatencies, 50);
    const ttsP95 = getPercentile(ttsLatencies, 95);
    const ttsStd = getStdDev(ttsLatencies);

    return (
      <div className="latency-section">
        {/* ===== Top Row ===== */}
        <Row className="g-4">
          <Col md="6" className="pe-2">
            <div className="latency-card">
              <h6 className="section-title">Active Services</h6>
              <div className="variables-grid">
                <div className="variable-item">
                  <span className="variable-label">LLM Model</span>
                  <span className="variable-value">{details?.model_name || '-'}</span>
                </div>

                <div className="variable-item">
                  <span className="variable-label">Channel Type</span>
                  <span className="variable-value">{details?.channel_type || '-'}</span>
                </div>

                <div className="variable-item">
                  <span className="variable-label">TTS Service</span>
                  <span className="variable-value">{details?.tts_service || '-'}</span>
                </div>

                <div className="variable-item">
                  <span className="variable-label">ASR/STT Service</span>
                  <span className="variable-value">
                    {details?.asr_service || details?.stt_service || '-'}
                  </span>
                </div>
              </div>
            </div>
          </Col>

          <Col md="6" className="ps-2">
            <div className="latency-card">
              <h6 className="section-title">Latency Profile</h6>

              <div className="latency-grid">
                <div className="metric-box">
                  <h4>{totalLLM.toFixed(2)}s</h4>
                  <span>Total LLM Latency</span>
                </div>

                <div className="metric-box success">
                  <h4>{totalTTS.toFixed(2)}s</h4>
                  <span>Total TTS Latency</span>
                </div>

                <div className="metric-box">
                  <h4>{avgLLM.toFixed(2)}s</h4>
                  <span>Average LLM Latency</span>
                </div>

                <div className="metric-box success">
                  <h4>{totalTTSDuration.toFixed(2)}s</h4>
                  <span>Total TTS Duration</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ===== Stats Row ===== */}
        <Row className="g-4 mt-2">
          <Col md="6" className="mt-2 pe-2">
            <div className="latency-card">
              <h6 className="section-title">LLM Latency Statistics</h6>
              <div className="stat-row">
                <div className="mini-box">
                  <strong>{llmP50.toFixed(2)}s</strong>
                  <span>P50</span>
                </div>
                <div className="mini-box">
                  <strong>{llmP95.toFixed(2)}s</strong>
                  <span>P95</span>
                </div>
                <div className="mini-box">
                  <strong>{llmStd.toFixed(2)}s</strong>
                  <span>Std Dev</span>
                </div>
              </div>
            </div>
          </Col>

          <Col md="6" className="mt-2 ps-2">
            <div className="latency-card">
              <h6 className="section-title">TTS Latency Statistics</h6>
              <div className="stat-row">
                <div className="mini-box">
                  <strong>{ttsP50.toFixed(2)}s</strong>
                  <span>P50</span>
                </div>
                <div className="mini-box">
                  <strong>{ttsP95.toFixed(2)}s</strong>
                  <span>P95</span>
                </div>
                <div className="mini-box">
                  <strong>{ttsStd.toFixed(2)}s</strong>
                  <span>Std Dev</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ===== Detailed Breakdown ===== */}
        <Row className="mt-3">
          <Col>
            <div className="latency-card">
              <h6 className="section-title">Detailed Breakdown</h6>

              <div className="table-responsive">
                <table className="latency-table">
                  <thead>
                    <tr>
                      <th>Interaction</th>
                      <th>LLM (s)</th>
                      <th>TTS Latency (s)</th>
                      <th>TTS Duration (s)</th>
                      <th>Total (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interactions.map((row, idx) => (
                      <tr key={idx}>
                        <td>#{row.interaction_sequence}</td>

                        <td>
                          {row.latency_llm?.toFixed(2) || '0.00'}
                          {row.total_tokens ? ` (${row.total_tokens} tokens)` : ''}
                        </td>

                        <td>{row.tts_time?.toFixed(2) || '0.00'}</td>

                        <td>{row.tts_speaking_duration?.toFixed(2) || '0.00'}</td>

                        <td>{((row.latency_llm || 0) + (row.tts_time || 0)).toFixed(2)}</td>
                      </tr>
                    ))}

                    <tr className="average-row">
                      <td>
                        <strong>Average</strong>
                      </td>
                      <td>{avgLLM.toFixed(2)}</td>
                      <td>{avgTTS.toFixed(2)}</td>
                      <td>{avgTTSDuration.toFixed(2)}</td>
                      <td>{avgTotal.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const interactionTab = () => {
    if (!messages.length) {
      return <div className="analysis-card">No interactions found.</div>;
    }

    // Group messages into interactions (user + assistant pairs)
    const interactions = [];
    for (let i = 0; i < messages.length; i++) {
      const current = messages[i];
      if (current.type === 'user') {
        const next = messages[i + 1];
        interactions.push({
          user: current,
          assistant: next?.type === 'assistant' ? next : null,
          timestamp: current.timestamp
        });
        if (next?.type === 'assistant') i++; // Skip the assistant message in next iteration
      } else {
        interactions.push({
          user: null,
          assistant: current,
          timestamp: current.timestamp
        });
      }
    }

    return (
      <div className="interaction-wrapper">
        {interactions.map((item, index) => (
          <div className="interaction-card" key={index}>
            <div className="interaction-header">
              <div>
                <div className="interaction-title">Interaction #{index + 1}</div>
                <div className="interaction-meta">
                  {new Date(item.timestamp).toLocaleTimeString()}
                </div>
              </div>

              <div className="interaction-meta">
                <div>
                  Total Response Time: {details?.total_response_time?.toFixed(2) || '0.00'}s
                </div>
                <div>Total Tokens: {details?.total_tokens || 0}</div>
              </div>
            </div>

            {item.user && (
              <div className="message-box">
                <div className="message-label">Caller</div>
                {item.user.text}
              </div>
            )}

            {item.assistant && (
              <div className="message-box message-assistant">
                <div className="message-label">Assistant</div>
                {item.assistant.text}
              </div>
            )}

            <div className="score-grid">
              <div className="score-card">
                <div className="score-label">Intent Score</div>
                <div className="score-value">{details?.metric_score_intent ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">Relevance Score</div>
                <div className="score-value">{details?.metric_score_relevance ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">Coherence Score</div>
                <div className="score-value">{details?.metric_score_coherence ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">Latency Score</div>
                <div className="score-value">{details?.metric_score_latency ?? 0}</div>
              </div>

              <div className="score-card">
                <div className="score-label">LLM Latency Score</div>
                <div className="score-value">0.00s</div>
              </div>

              <div className="score-card">
                <div className="score-label">ASR Latency Score</div>
                <div className="score-value">0.00s</div>
              </div>

              <div className="score-card">
                <div className="score-label">TTS Latency Score</div>
                <div className="score-value">0.00s</div>
              </div>
            </div>

            <div className="evaluation-section">
              <div className="evaluation-title">Evaluation</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleDownloadChatPDF = async () => {
    const canvas = await html2canvas(chatRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    pdf.save(`chat-transcript-${callId}.pdf`);
  };

  const handleDownloadCSV = () => {
    if (!details) return;

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const formatLabel = (text) =>
      text
        ?.split('_')
        ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        ?.join(' ');

    const baseData = {
      'Call Date': formattedCallTime,
      Name: details?.agentName || '',
      'Phone Number': details?.phoneNumber || '',
      Duration: `${details?.duration || 0}s`,
      Status: details?.status || '',
      Disposition: details?.disposition || '',
      'Analysis Summary': details?.summary || ''
    };

    const structuredData = {};
    const { highlighted_points, ...otherVariables } = details.structuredData || {};

    if (Array.isArray(highlighted_points) && highlighted_points.length > 0) {
      structuredData['Highlighted Points'] = JSON.stringify(highlighted_points);
    }

    Object.entries(otherVariables).forEach(([key, value]) => {
      structuredData[formatLabel(key)] =
        typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || '-';
    });

    const finalData = {
      ...baseData,
      ...structuredData,
      Notes: details?.notes || '',
      Transcript: details?.transcript || ''
    };

    const headers = Object.keys(finalData);
    const row = Object.values(finalData).map(escapeCSV);

    const csvContent = headers.join(',') + '\n' + row.join(',');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `AI-Report-${callId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ================= HELPERS ================= */
  function formatTime(value) {
    if (value === undefined || value === null) return '0:00';
    const totalSeconds = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(totalSeconds)) return '0:00';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered>
      <ModalHeader toggle={onClose}>Call Details</ModalHeader>
      {isLoading ? (
        <ModalBody className="call-details-modal">
          <div
            className="p-0 d-flex justify-content-center align-items-center m-0"
            style={{ height: 'calc(100vh - 200px)' }}>
            <CallexLoader />
          </div>
        </ModalBody>
      ) : (
        <ModalBody className="call-details-modal">
          {/* ================= TOP MODE SWITCH ================= */}
          <div className="d-flex justify-content-between align-items-baseline">
            <div className="mode-switch">
              <div className={`mode-indicator ${mode === 'simple' ? 'left' : 'right'}`} />

              <div
                className={`mode-card ${mode === 'simple' ? 'active' : ''}`}
                onClick={() => {
                  setMode('simple');
                  setActiveTab('chat');
                }}>
                Simple
              </div>

              <div
                className={`mode-card ${mode === 'advanced' ? 'active' : ''}`}
                onClick={() => {
                  setMode('advanced');
                  setActiveTab('interaction');
                }}>
                Advanced
              </div>
            </div>
            <Button
              id="downloadChatBtn"
              color="primary"
              size="sm"
              className="d-flex align-items-center py-2 px-2"
              onClick={handleDownloadCSV}>
              <Icon.Download size={16} />
            </Button>
          </div>

          {/* ================= COMMON INFO ================= */}
          <div className="common-info">
            <Row>
              <Col md="3">
                <div className="info-card">
                  <div className="info-label">Source</div>
                  <div className="info-value">{details?.agentName || '-'}</div>
                </div>
              </Col>

              <Col md="3">
                <div className="info-card">
                  <div className="info-label">Phone Number</div>
                  <div className="info-value">{details?.phoneNumber || '-'}</div>
                </div>
              </Col>

              <Col md="3">
                <div className="info-card">
                  <div className="info-label">Call Time</div>
                  <div className="info-value">{formattedCallTime}</div>
                </div>
              </Col>

              <Col md="3">
                <div className="info-card">
                  <div className="info-label">Call Info</div>
                  <div className="info-value">
                    <span>{formatTime(details?.duration)}</span>
                    <span>{details?.disposition}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* ================= TABS ================= */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <Nav tabs className="inner-tabs call-details-tab">
                {mode === 'simple' ? (
                  <>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'chat' })}
                        onClick={() => setActiveTab('chat')}>
                        Chat
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'analysis' })}
                        onClick={() => setActiveTab('analysis')}>
                        Analysis
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'latency' })}
                        onClick={() => setActiveTab('latency')}>
                        Latency Profile
                      </NavLink>
                    </NavItem>
                  </>
                ) : (
                  <>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'interaction' })}
                        onClick={() => setActiveTab('interaction')}>
                        Interaction
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'evaluations' })}
                        onClick={() => setActiveTab('evaluations')}>
                        Evaluations
                      </NavLink>
                    </NavItem>
                    {/* <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'llm' })}
                        onClick={() => setActiveTab('llm')}>
                         LLM Details & Cost
                      </NavLink>
                    </NavItem> */}
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === 'latency' })}
                        onClick={() => setActiveTab('latency')}>
                        Latency Profile
                      </NavLink>
                    </NavItem>
                  </>
                )}
              </Nav>

              {activeTab === 'chat' && (
                <Button
                  id="downloadChatBtn"
                  color="primary"
                  size="sm"
                  className="d-flex align-items-center py-2 px-2"
                  onClick={handleDownloadChatPDF}>
                  <Icon.Download size={16} className="me-1" />
                  Export Chat
                </Button>
              )}
            </div>

            <TabContent activeTab={activeTab} className="pt-3">
              <TabPane tabId="chat">{renderChat()}</TabPane>
              <TabPane tabId="analysis">{renderAnalysis()}</TabPane>
              <TabPane tabId="latency">{renderLatency()}</TabPane>
              <TabPane tabId="interaction">{interactionTab()}</TabPane>
              <TabPane tabId="evaluations">{renderEvaluations()}</TabPane>
              <TabPane tabId="llm">{renderLlmDetails()}</TabPane>
            </TabContent>
          </div>
        </ModalBody>
      )}
    </Modal>
  );
};

CallexCallDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  callId: PropTypes.string
};

export default CallexCallDetailsModal;
